import type { Partner, PartnerType } from "@stockline/shared/types/crm/partner"
import { offlineDb } from "../../../lib/offline/db"
import { PARTNER_TABLE } from "../../../lib/offline/resources/crm/partner/db"
import { useSharedLiveQuery } from "../../../lib/offline/use-shared-live-query"

export interface UsePartnersFilter {
  type?: PartnerType
  isActive?: boolean
}

const EMPTY_PARTNERS: Partner[] = []

function partnersCacheKey(filter?: UsePartnersFilter): string {
  return filter
    ? `partners:type=${filter.type ?? ""}:isActive=${filter.isActive ?? ""}`
    : "partners:all"
}

function partnersQueryFactory(filter?: UsePartnersFilter) {
  return async () => {
    const table = offlineDb.table<Partner>(PARTNER_TABLE)
    // `updatedAt` isn't indexed on the partners table — fetch then JS-sort
    // descending to mirror the legacy `reactive.useQuery` ("-updatedAt")
    // behaviour. The shared cache keeps this to once per filter combo.
    const all = await table.toArray()
    // Tombstone filter — delta-pulls upsert soft-deleted partners
    // back into Dexie with `deletedAt` set; hiding them here is what
    // makes an SSE `partner.deleted` visibly drop the card.
    const live = all.filter(
      (p) => !(p as Partner & { deletedAt?: string | null }).deletedAt
    )
    const filtered = filter
      ? live.filter((p) => {
          if (filter.type !== undefined && p.type !== filter.type) return false
          if (
            filter.isActive !== undefined &&
            (p as { isActive?: boolean }).isActive !== filter.isActive
          ) {
            return false
          }
          return true
        })
      : live
    return filtered.sort((a, b) => {
      const au = (a as Partner & { updatedAt?: string | null }).updatedAt
      const bu = (b as Partner & { updatedAt?: string | null }).updatedAt
      if (au == null || bu == null) return 0
      return au < bu ? 1 : au > bu ? -1 : 0
    })
  }
}

/**
 * Reactive global partners list. Shared across all consumers via
 * `useSharedLiveQuery` so the ~1,500-row scan happens once per filter
 * combination — every recap `<PartnerButton>` and `<PartnerGroup>` reads
 * the same cached array. A remount on virtualizer scroll-out + back snaps
 * back to the cached value with no IDB work.
 */
export function usePartners(filter?: UsePartnersFilter): Partner[] {
  const rows = useSharedLiveQuery<Partner[]>(
    partnersCacheKey(filter),
    partnersQueryFactory(filter)
  )
  return rows ?? EMPTY_PARTNERS
}

/**
 * Whether the shared partners cache for `filter` has not yet emitted its
 * first value — i.e. Dexie hydration is still in flight. Shares the exact
 * same cache key (and query) as `usePartners`, so `useSharedLiveQuery`'s
 * per-key dedup means calling both hooks for the same filter in one
 * component costs nothing extra: the underlying `liveQuery`/IDB scan runs
 * once, whichever hook mounts first creates the shared entry, and the other
 * just reads/subscribes to it.
 *
 * Consumers that need to distinguish "still loading" from "genuinely empty
 * catalog" (e.g. a picker that says "Loading…" instead of "No matches")
 * should use this instead of inferring loading from `options.length === 0`,
 * which would hang forever on an empty-but-loaded catalog.
 */
export function usePartnersLoading(filter?: UsePartnersFilter): boolean {
  const rows = useSharedLiveQuery<Partner[]>(
    partnersCacheKey(filter),
    partnersQueryFactory(filter)
  )
  return rows === undefined
}
